// ApplicationViewDepartmentHead.jsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from '../api/axios';
import './ApplicationViewDepartmentHead.css';

export default function ApplicationViewDepartmentHead({ applicationId, onClose, locale = 'kk' }) {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingFileKey, setLoadingFileKey] = useState(null);
  const [fileProgress, setFileProgress] = useState({});

  const apiUrl = process.env.REACT_APP_API_URL || 'https://expertise.gov.kz';
  const fixedLocale = ['kk', 'kz'].includes(locale) ? 'kk' : 'ru';

  const t = useMemo(() => ({
    kk: {
      applicationInfo: 'Өтінім туралы ақпарат', title: 'Атауы', subject: 'Пән', category: 'Санат',
      grade: 'Сынып', devLang: 'Әзірлеу тілі', teachLang: 'Оқу тілі', publisher: 'Баспа (шифр)',
      authors: 'Авторы', files: 'Файлдар', section: 'бөлім', download: 'жүктеу', logs: 'Логтар',
      close: 'Жабу', criteria_form: 'Өңдеу формасы', direction: 'Бағыт',
      publication_type: 'Басылым түрі', education_level: 'Білім деңгейі', format: 'Формат',
      service_type: 'Қызмет түрі', month: 'Ай', year: 'Жыл', status: 'Күйі',
      created_at: 'Жасалған күні', message: 'Хабарлама', reject_reason: 'Қайтару себебі',
    },
    ru: {
      applicationInfo: 'Информация о заявке', title: 'Название', subject: 'Предмет', category: 'Категория',
      grade: 'Класс', devLang: 'Язык разработки', teachLang: 'Язык обучения', publisher: 'Издательство (шифр)',
      authors: 'Авторы', files: 'Файлы', section: 'раздел', download: 'скачать', logs: 'Логи',
      close: 'Закрыть', criteria_form: 'Форма разработки', direction: 'Направление',
      publication_type: 'Тип издания', education_level: 'Уровень образования', format: 'Формат',
      service_type: 'Тип услуги', month: 'Месяц', year: 'Год', status: 'Статус',
      created_at: 'Создано', message: 'Сообщение', reject_reason: 'Причина отказа',
    },
  }[fixedLocale]), [fixedLocale]);

  /* ===== helpers ===== */
  const pickLocale = useCallback((val) => {
    if (val == null) return '—';
    if (typeof val === 'string' || typeof val === 'number') return String(val);
    if (typeof val === 'object') {
      const kzLike = val.kk ?? val.kz ?? val.name_kz ?? val.title_kz;
      const ruLike = val.ru ?? val.name_ru ?? val.title_ru;
      const primary = fixedLocale === 'kk' ? (kzLike ?? ruLike) : (ruLike ?? kzLike);
      if (typeof primary === 'string' || typeof primary === 'number') return String(primary);
      for (const v of Object.values(val)) if (typeof v === 'string' || typeof v === 'number') return String(v);
      return '—';
    }
    return String(val);
  }, [fixedLocale]);

  const normalizeApplication = useCallback((app) => {
    if (!app || typeof app !== 'object') return app;
    const out = { ...app };
    for (const [k, v] of Object.entries(app)) {
      if (['files', 'decision_last', 'decision_history', 'group', 'approvals',
        'signatures', 'logs', 'archive', 'decision'].includes(k)) continue;
      out[k] = pickLocale(v);
    }
    out.status = pickLocale(app.status);
    return out;
  }, [pickLocale]);

  const ddmmyyyy = (s) => {
    if (!s || typeof s !== 'string') return '—';
    const [date] = s.split(' ');
    const [yyyy, mm, dd] = (date || '').split('-');
    return (yyyy && mm && dd) ? `${dd}.${mm}.${yyyy}` : s;
  };

  const absoluteUrl = (u) => (u?.startsWith('http') ? u : `${apiUrl}${u || ''}`);

  const isPresignedMinioUrl = (url) => {
    if (!url) return false;
    const lower = String(url).toLowerCase();
    return (
      lower.includes('x-amz-signature=') ||
      lower.includes('x-amz-algorithm=') ||
      lower.includes('x-amz-credential=') ||
      lower.includes('minio.expertise.gov.kz')
    );
  };

  const saveBlob = (blob, filename = 'file') => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const parseFilenameFromDisposition = (disp) => {
    if (!disp) return null;
    const mStar = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disp);
    if (mStar?.[1]) return decodeURIComponent(mStar[1].replace(/^["']|["']$/g, ''));
    const m = /filename="?([^"]+)"?/i.exec(disp);
    return m ? m[1] : null;
  };

  const setPct = (key, pct, indeterminate = false) =>
    setFileProgress((p) => ({ ...p, [key]: { pct, indeterminate } }));

  const clearPct = (key) =>
    setFileProgress((p) => { const cp = { ...p }; delete cp[key]; return cp; });

  const progressKey = (file) => String(file?.id ?? file?.download_url ?? file?.url ?? Math.random());

  /* ===== download by file ID (backend API) ===== */
  const downloadFileById = async (fileId, suggestedName = 'file') => {
    const key = String(fileId);
    try {
      setLoadingFileKey(key);
      setPct(key, 0, false);

      const response = await axios.get(`/ebmso/files/${fileId}/download/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          Accept: 'application/octet-stream',
          'Accept-Language': fixedLocale,
        },
        responseType: 'blob',
        onDownloadProgress: (e) => {
          if (e?.total) {
            setPct(key, Math.min(100, Math.round((e.loaded / e.total) * 100)), false);
          } else {
            setPct(key, 100, true);
          }
        },
      });

      const disp = response.headers['content-disposition'];
      saveBlob(response.data, parseFilenameFromDisposition(disp) || suggestedName);
    } catch (error) {
 
      alert(fixedLocale === 'kk' ? '❌ Файлды жүктеу мүмкін болмады' : '❌ Не удалось скачать файл');
    } finally {
      setLoadingFileKey(null);
      clearPct(key);
    }
  };

  /* ===== download by URL (MinIO or direct) ===== */
  const downloadByUrlWithProgress = async (url, suggestedName, key) => {
    const fullUrl = absoluteUrl(url);
    const isMinio = isPresignedMinioUrl(fullUrl);

    try {
      setLoadingFileKey(key);
      setPct(key, 0, false);

      const headers = { 'Accept-Language': fixedLocale };
      // MinIO presigned URLs уже содержат подпись — Authorization: Bearer сломает запрос
      if (!isMinio) {
        headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      }

      const res = await fetch(fullUrl, { headers });
      if (!res.ok || !res.body) throw new Error(`Bad response: ${res.status}`);

      const total = Number(res.headers.get('Content-Length')) || 0;
      const reader = res.body.getReader();
      const chunks = [];
      let received = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.byteLength;
        if (total > 0) {
          setPct(key, Math.min(100, Math.round((received / total) * 100)), false);
        } else {
          setPct(key, 100, true);
        }
      }

      saveBlob(new Blob(chunks), suggestedName);
    } catch (e) {

      if (isMinio) {
        // fallback — открываем напрямую
        const a = document.createElement('a');
        a.href = fullUrl;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.download = suggestedName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        alert(fixedLocale === 'kk' ? '❌ Файлды жүктеу мүмкін болмады' : '❌ Не удалось скачать файл');
      }
    } finally {
      setLoadingFileKey(null);
      clearPct(key);
    }
  };

  /* ===== data fetch ===== */
  useEffect(() => {
    if (!applicationId) return;
    const ctrl = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/backoffice/applications/${applicationId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Accept-Language': fixedLocale,
          },
          signal: ctrl.signal,
        });

        const payload = res.data ?? {};
        const base = payload.application ?? payload.data ?? payload;

        const normalized = {
          ...base.group,
          ...base,
          files: base.files ?? [],
          status: base.internal_status ?? base.public_status ?? base.status,
        };

        setApplication(normalizeApplication(normalized));
      } catch (err) {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          alert(fixedLocale === 'kk' ? 'Өтінішті ашу қатесі!' : 'Ошибка открытия заявки!');
          onClose?.();
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [applicationId, fixedLocale, normalizeApplication, onClose]);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  /* ===== loading / empty states ===== */
  if (loading) return (
    <div className="avh-backdrop" onClick={onClose}>
      <div className="avh" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="avh-close-x" onClick={onClose}>×</button>
        <p className="avh-loading">{fixedLocale === 'kk' ? 'Жүктелуде...' : 'Загрузка...'}</p>
      </div>
    </div>
  );

  if (!application) return (
    <div className="avh-backdrop" onClick={onClose}>
      <div className="avh" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="avh-close-x" onClick={onClose}>×</button>
        <p className="avh-loading">{fixedLocale === 'kk' ? 'Жүктелуде...' : 'Загрузка...'}</p>
      </div>
    </div>
  );

  /* ===== render ===== */
  return (
    <div className="avh-backdrop" onClick={onClose}>
      <div className="avh" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>

        <button type="button" className="avh-close-x" aria-label={t.close} onClick={onClose}>×</button>

        <h2 className="avh__title">📄 {t.applicationInfo}</h2>

        <table className="avh-table">
          <tbody>
            <tr><td className="avh-td avh-td--label">🏷️ {t.category}</td><td className="avh-td avh-td--value">{application.category}</td></tr>
            <tr><td className="avh-td avh-td--label">🧩 {t.criteria_form}</td><td className="avh-td avh-td--value">{application.criteria_form}</td></tr>
            <tr><td className="avh-td avh-td--label">📐 {t.format}</td><td className="avh-td avh-td--value">{application.format}</td></tr>
            <tr><td className="avh-td avh-td--label">🔖 {t.status}</td><td className="avh-td avh-td--value">{application.status || '—'}</td></tr>
            <tr><td className="avh-td avh-td--label">🗓️ {t.created_at}</td><td className="avh-td avh-td--value">{ddmmyyyy(application.created_at)}</td></tr>
          </tbody>
        </table>

        {Array.isArray(application.files) && application.files.length > 0 && (
          <div className="avh-files">
            <h4 className="avh-files__title">📎 {t.files}</h4>
            <ul className="avh-files__list">
              {application.files.map((file, index) => {
                // --- всё про файл вычисляем ВНУТРИ map ---
                const sectionNum = file.section ?? index + 1;
                const sectionLabel = file.name || `${sectionNum}-${t.section}`;

                const isLink = file.is_link === true;
                const dlUrl = file.download_url || file.url;
                const linkUrl = file.link_url || file.external_url || '';

                const key = progressKey(file);
                const prog = fileProgress[key];
                const isLoading = loadingFileKey === String(file.id ?? key);

                const archiveUrl =
                  file.section_archive_download_url ||
                  file.section_archive?.download_url ||
                  null;
                const archiveName =
                  file.section_archive_name ||
                  file.section_archive?.original_name ||
                  `section_${sectionNum}.zip`;
                const archiveKey = `archive_${file.id ?? index}`;
                const archiveProg = fileProgress[archiveKey];
                const archiveLoading = loadingFileKey === archiveKey;

                const ProgressButton = ({ loading: btnLoading, prog: btnProg, label, icon, onClick, disabled }) => (
                  <button
                    className="avh-btn avh-btn--download"
                    disabled={disabled || btnLoading}
                    onClick={onClick}
                  >
                    {btnLoading ? (
                      <span className="avh-dl-wrap">
                        <span className="avh-dl-row">
                          <span className="spinner" />
                          <span className="avh-dl-label">
                            ⬇️ {btnProg?.indeterminate ? '...' : `${btnProg?.pct ?? 0}%`}
                          </span>
                        </span>
                        <span
                          className={`avh-progress ${btnProg?.indeterminate ? 'indeterminate' : ''}`}
                          style={!btnProg?.indeterminate ? { '--w': `${btnProg?.pct ?? 0}%` } : undefined}
                          aria-hidden="true"
                        >
                          <span className="avh-progress__bar" />
                        </span>
                      </span>
                    ) : (
                      <>{icon} {label} ({t.download})</>
                    )}
                  </button>
                );

                return (
                  <li key={file.id || index} className="avh-files__item">

                    {isLink ? (
                      <>
                        {linkUrl && (
                          <a
                            className="avh-btn avh-btn--link"
                            href={absoluteUrl(linkUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            🔗 {sectionLabel}
                          </a>
                        )}
                        {archiveUrl && (
                          <ProgressButton
                            loading={archiveLoading}
                            prog={archiveProg}
                            icon="📦"
                            label={archiveName}
                            onClick={() => downloadByUrlWithProgress(archiveUrl, archiveName, archiveKey)}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <ProgressButton
                          loading={isLoading}
                          prog={prog}
                          icon="📄"
                          label={sectionLabel}
                          onClick={() => {
                            const suggestedName = `${(application.publisher || 'file').toLowerCase()}_${sectionNum}.pdf`;
                            if (dlUrl && isPresignedMinioUrl(dlUrl)) {
                              // MinIO presigned URL — идём напрямую без Bearer токена
                              downloadByUrlWithProgress(dlUrl, suggestedName, String(key));
                            } else if (file.id) {
                              // обычный backend endpoint — через axios с токеном
                              downloadFileById(file.id, suggestedName);
                            } else if (dlUrl) {
                              downloadByUrlWithProgress(dlUrl, suggestedName, String(key));
                            }
                          }}
                        />
                        {archiveUrl && (
                          <ProgressButton
                            loading={archiveLoading}
                            prog={archiveProg}
                            icon="📦"
                            label={archiveName}
                            onClick={() => downloadByUrlWithProgress(archiveUrl, archiveName, archiveKey)}
                          />
                        )}
                      </>
                    )}

                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {application?.word && (
  <div className="avh-files">
    <h4 className="avh-files__title">
      {fixedLocale === 'kk' ? '📝 Доработка файлы' : '📝 Файл доработки'}
    </h4>
    <ul className="avh-files__list">
      <li className="avh-files__item">
        <button
          className="avh-btn avh-btn--download"
          onClick={() => {
            const wordName = application.word?.original_name || 'document.docx';
            fetch(`${apiUrl}/api/applications/${applicationId}/download-word`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            })
              .then(r => r.blob())
              .then(blob => saveBlob(blob, wordName))
              .catch(() => alert(fixedLocale === 'kk' ? '❌ Жүктеу мүмкін болмады' : '❌ Не удалось скачать'));
          }}
        >
          📄 {application.word?.original_name || 'document.docx'} ({t.download})
        </button>
      </li>
    </ul>
  </div>
)}

        <div className="avh-actions">
          <button className="avh-btn avh-btn--close" onClick={onClose}>{t.close}</button>
        </div>

      </div>
    </div>
  );
}